import { EmailInvitationPreview as NewJoinersAndAllocationsInvitationPreview } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/EmailInvitationPreview';
import { NewJoinersAndAllocationsContracts } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsContracts/NewJoinersAndAllocationsContracts';
import { NewJoinersAndAllocationsGeneralDealInputs } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsGeneralDealInputs/NewJoinersAndAllocationsGeneralDealInputs';
import { NewJoinersAndAllocationsIntroductionStep } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsIntroductionStep/NewJoinersAndAllocationsIntroductionStep';
import { NewJoinersAndAllocationsInvestors } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsInvestors/NewJoinersAndAllocationsInvestors';
import { NewJoinersAndAllocationsInvestorsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsInvestorsSummary/NewJoinersAndAllocationsInvestorsSummary';
import {
	INVITATION_PREVIEW_INPUTS_FORM,
	useNewJoinersAndAllocationsInvitationPreviewController as useJoinersAndAllocationsInvitationPreviewController,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsInvitationPreview/useNewJoinersAndAllocationsInvitationPreviewController';
import { NewJoinersAndAllocationsKYC as NewJoinersAndAllocationsKyc } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsKYC/NewJoinersAndAllocationsKYC';
import { NewJoinersAndAllocationsPersonalDataRequirements } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/NewJoinersAndAllocationsStepsComponents/NewJoinersAndAllocationsPersonalData/NewJoinersAndAllocationsPersonalDataRequirements';
import { useNewJoinersAndAllocationsModalController } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/NewJoinersAndAllocationsModal/useNewJoinersAndAllocationsModalController';
import { DynamicContinueButton } from '@dealstack/frontend/shared/components/DynamicContinueButton/DynamicContinueButton';
import { Button } from '@dealstack/frontend/shared/components/fundament/Button/Button';
import { ButtonsApart } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import { BackButton } from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Divider } from '@dealstack/frontend/shared/components/fundament/Divider/Divider';
import { Modal } from '@dealstack/frontend/shared/components/fundament/Modal/Modal';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { IndividualSchedule } from '@dealstack/frontend/shared/components/IndividualSchedule/IndividualSchedule';
import { SectionHeader } from '@dealstack/frontend/shared/components/SectionHeader/SectionHeader';
import {
	useTranslationMipCreate,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { createContext, useMemo } from 'react';
import styled from 'styled-components';

interface Props {
	className?: string;
}

export const NewJoinersAndAllocationsModalContext = createContext<
	ReturnType<typeof useNewJoinersAndAllocationsModalController>
>({} as ReturnType<typeof useNewJoinersAndAllocationsModalController>);

const NewJoinersAndAllocationsModal = ({ className }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tMipCreate = useTranslationMipCreate();
	const newJoinersAndAllocationsModalController = useNewJoinersAndAllocationsModalController();
	const {
		onPreviousStepClicked,
		currentStep,
		isFinalStep,
		disableContinue,
		onNextStepClicked,
		setShowNewJoinersAndAllocationsModal,
		mipData,
		isNewJoinersModal,
		inviteTemplateForm,
		totalStepsToDisplay,
		currentStepToDisplay,
	} = newJoinersAndAllocationsModalController;

	const { invitationPreviewForm } = useJoinersAndAllocationsInvitationPreviewController(
		{
			inviteTemplateForm,
		},
		isNewJoinersModal
	);

	const previewText = invitationPreviewForm.watch(INVITATION_PREVIEW_INPUTS_FORM.EMAIL_BODY_TEXT);

	const currentStepComponent = useMemo(() => {
		switch (currentStep) {
			case 0:
				return <NewJoinersAndAllocationsIntroductionStep />;
			case 1:
				return <NewJoinersAndAllocationsGeneralDealInputs />;
			case 2:
				return <NewJoinersAndAllocationsPersonalDataRequirements />;
			case 3:
				return <NewJoinersAndAllocationsKyc />;
			case 4:
				return <NewJoinersAndAllocationsContracts />;
			case 5:
				return <IndividualSchedule />;
			case 6:
				return (
					<NewJoinersAndAllocationsInvitationPreview
						className={className}
						form={invitationPreviewForm}
						inputName={INVITATION_PREVIEW_INPUTS_FORM.EMAIL_BODY_TEXT}
						bodyText={previewText}
						previewTitle={
							!isNewJoinersModal
								? tMipManage('new_joiner.allocation.preview.title')
								: tMipCreate('invite.preview.title')
						}
						emailPreviewTitle={
							!isNewJoinersModal
								? tMipManage('new_joiner.allocation.preview.email.title')
								: tMipCreate('invite.preview.email.title')
						}
						emailPreviewChild={
							!isNewJoinersModal ? (
								<div>
									<Button size="medium" variant="secondary">
										{tMipManage('new_joiner.allocation.preview.email.button')}
									</Button>
								</div>
							) : (
								<div>
									<Button size="medium" variant="primary">
										{tMipCreate('invite.preview.buttons.accept')}
									</Button>
								</div>
							)
						}
					/>
				);
			case 7:
				return <NewJoinersAndAllocationsInvestors />;
			default:
				return null;
		}
	}, [currentStep, previewText]);

	const modalTitle = isNewJoinersModal
		? tMipManage('investors.upsert_investor_shares_modal.new_joiner')
		: tMipManage('investors.upsert_investor_shares_modal.new_allocation');

	return (
		<NewJoinersAndAllocationsModalContext.Provider
			value={newJoinersAndAllocationsModalController}
		>
			{isFinalStep ? (
				<NewJoinersAndAllocationsInvestorsSummary />
			) : (
				<Modal
					size={isFinalStep ? 'sm' : 'lg'}
					reactModalProps={{
						shouldCloseOnOverlayClick: false,
					}}
					onClose={() => {
						setShowNewJoinersAndAllocationsModal(false);
					}}
					className={className}
				>
					<>
						<SectionHeader
							title={modalTitle}
							overtitle={mipData?.entity?.displayName}
							size="h4b"
						/>

						<ContentWrapper>{currentStepComponent}</ContentWrapper>

						<FooterWrapper>
							<Divider />
							<ButtonsApart>
								<BackButton
									disabled={currentStep === 0}
									onClick={onPreviousStepClicked}
								/>
								<Text
									colorFaded
									variant="p-sm"
									data-testid={TEST_ELEMENT_IDS.MODAL_PROGRESS_TEXT}
								>
									{tMipManage('investors.upsert_investor_shares_modal.progress', {
										currentStep: currentStepToDisplay,
										maxSteps: totalStepsToDisplay,
									})}
								</Text>
								<DynamicContinueButton
									withPopover={currentStep === 1 && disableContinue}
									message={tMipManage('send_modal.continue_disabled_info')}
									disabled={isFinalStep || disableContinue}
									onClick={onNextStepClicked}
								/>
							</ButtonsApart>
						</FooterWrapper>
					</>
				</Modal>
			)}
			;
		</NewJoinersAndAllocationsModalContext.Provider>
	);
};

const FooterWrapper = styled.div`
	margin-top: auto;

	${Divider} {
		margin: 2em 0 1em 0;
	}
`;
const ContentWrapper = styled.div`
	position: relative;
`;

const Styled = styled(NewJoinersAndAllocationsModal)`
	${IndividualSchedule} {
		margin: 1em 0 3em 0;
	}
`;

export { Styled as NewJoinersAndAllocationsModal };
